import {
  Box,
  Button,
  DropdownOption,
  Input,
  ThemeOverride
} from "@modernatx-internal/ui-kit-react";
import { useRouter } from "next/router";
import React from "react";

import { useLocationSearch } from "@/finder/hooks/V1/useLocationSearch";
import { LocationFinderProps } from "@/types/Block";

export const LocationFinder: React.FC<LocationFinderProps> = ({
  button,
  clientGeolocationErrorMessages,
  detectLocationDropdownLabel,
  finderLocation = "/finder",
  input,
  inputOverrideMode,
  layout = "horizontal",
  mode = "light",
  product = "",
  v2Product
}) => {
  const [searchValue, searchValueSet] = React.useState(input.value);
  const optionSelected = React.useRef<DropdownOption | null>(null);
  const router = useRouter();
  const formRef = React.useRef<HTMLFormElement>(null);

  // We reroute the location selection through the HTML Form element's `submit` event
  // listener to ensure Amplitude picks up the `Form Submit` event
  const handleSubmit = React.useCallback(() => {
    formRef.current?.dispatchEvent(
      new Event("submit", {
        bubbles: true,
        cancelable: true
      })
    );
  }, []);

  const handleSubmitForm = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (optionSelected.current) {
        const queryParams = new URLSearchParams({ placeId: String(optionSelected.current.value) });

        if (product) {
          queryParams.append("products", product);
        } else if (v2Product) {
          queryParams.append("vaccines", v2Product);
        }
        // Adding a slight delay to ensure the Amplitude Form Submit event is captured at the correct path before the route change
        setTimeout(() => router.push(`${finderLocation}?${queryParams.toString()}`), 100);
      }
    },

    [finderLocation, product, router, v2Product]
  );

  const searchOptionSelect = React.useCallback(
    (option: DropdownOption) => {
      optionSelected.current = option;
      handleSubmit();
    },
    [handleSubmit]
  );

  const {
    searchSuggestionsList,
    showNoResults,
    handleSearchChange,
    handleSearchSubmit,
    handleSearchSuggestionClick,
    searchError
  } = useLocationSearch({
    clientGeolocationErrorMessages,
    detectLocationDropdownLabel,
    searchValue,
    searchValueSet,
    searchOptionSelect
  });

  return (
    <>
      <ThemeOverride mode={mode}>
        <Box
          ref={formRef}
          as="form"
          data-track="finder-search-bar"
          sx={{
            display: "flex",
            flexDirection: ["column", "column", layout === "vertical" ? "column" : "row"],
            gap: 2,
            position: "relative"
          }}
          onSubmit={handleSubmitForm}
        >
          {/* This overide is an optional overide to correctly style the error messages. 
          This is needed for certain experiences such as the hero for products.modernatx.com/spikevax  */}
          <ThemeOverride mode={inputOverrideMode || mode}>
            <Input
              error={searchError}
              dropdownOptions={searchSuggestionsList}
              label={input.label}
              value={searchValue}
              type="select"
              sx={{ flex: 1 }}
              showNoResults={showNoResults}
              onChange={handleSearchChange}
              onSelectOption={handleSearchSuggestionClick}
              onEnter={handleSearchSubmit}
            />
          </ThemeOverride>
          <Button variant={button.variant} onClick={handleSearchSubmit}>
            {button.title}
          </Button>
        </Box>
      </ThemeOverride>
    </>
  );
};
